import { Link } from "react-router-dom";
import { Shield, CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserTier } from "@/hooks/useUserTier";

interface KycTierGateProps {
  requiredTier: number;
  /** Friendly name of the area being protected, e.g. "Wallets", "Withdrawals". */
  featureLabel?: string;
  children: React.ReactNode;
}

const REQUIREMENT_LABELS: Record<string, string> = {
  email_verified: "Verify your email address",
  profile_completed: "Complete your profile (name, address, date of birth, country)",
  consent_given: "Accept terms & compliance consent",
  phone_verified: "Verify your phone number",
  identity_document: "Submit a valid government-issued ID",
  proof_of_address: "Upload a recent proof of address",
  source_of_funds: "Provide source-of-funds documentation",
};

export default function KycTierGate({ requiredTier, featureLabel, children }: KycTierGateProps) {
  const { currentTier, tiers, loading, isRequirementMet } = useUserTier();

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton className="h-48 w-full max-w-2xl mx-auto" />
      </div>
    );
  }

  if (currentTier >= requiredTier) return <>{children}</>;

  const targetTier = tiers.find((t) => t.tier_level === requiredTier);
  const requirements = (targetTier?.requirements_json as string[] | undefined) ?? [
    "email",
    "profile",
    "consent",
  ];

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-4">
      <Card className="w-full max-w-2xl border-amber-500/30 bg-card">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-amber-500/10 p-2.5 border border-amber-500/30">
              <Shield className="h-6 w-6 text-amber-500" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-xl">Identity verification required</CardTitle>
              <CardDescription className="mt-1">
                {featureLabel
                  ? `Complete Tier ${requiredTier} verification to access ${featureLabel}.`
                  : `Complete Tier ${requiredTier} verification to continue.`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
            <span className="text-muted-foreground">Your tier</span>
            <span className="font-mono font-bold text-foreground">Tier {currentTier}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Required</span>
            <span className="font-mono font-bold text-amber-500">
              Tier {requiredTier}
              {targetTier?.name ? ` · ${targetTier.name}` : ""}
            </span>
          </div>

          <div>
            <p className="text-sm font-medium mb-2 text-foreground">Requirements</p>
            <ul className="space-y-2">
              {requirements.map((req) => {
                const met = isRequirementMet(req);
                const label =
                  REQUIREMENT_LABELS[
                    req === "email"
                      ? "email_verified"
                      : req === "profile"
                      ? "profile_completed"
                      : req === "consent"
                      ? "consent_given"
                      : req === "phone"
                      ? "phone_verified"
                      : req
                  ] ?? req;
                return (
                  <li key={req} className="flex items-start gap-2 text-sm">
                    {met ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    )}
                    <span className={met ? "text-muted-foreground line-through" : "text-foreground"}>
                      {label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button asChild className="flex-1">
              <Link to={`/app/kyc/${requiredTier}`}>
                Complete Verification <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild className="flex-1">
              <Link to="/app/dashboard">Back to Dashboard</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
